import React, { useContext, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import DataRoomDocumentsContext from '../../providers/DataRoomDocuments/DataRoomDocuments.context';
import DataRoomDocument from '../DataRoomDocument';
import { Checkbox, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import { Link, Params, useParams } from 'react-router-dom';
import ArrowupIcon from 'icons/Arrowup.icon';
import useCallbackRef from 'hooks/useCallbackRef';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import DataRoomDocEmpty from '../DataRoomDocEmpty';
import useQueryParams from 'hooks/useQueryParams';
import useMatchMedia from 'hooks/useMatchMedia';
import SortIcon from 'icons/Sort.icon';
import useFileDownload from 'hooks/useFileDownload';
import DataRoomActionMenu from '../DataRoomActionMenu';
import useDeleteFile from '../../hooks/useDeleteFile';
import DataRoomSelectedDocumentsContext from '../../providers/DataRoomSelectedDocuments/DataRoomSelectedDocuments.context';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import MymeTypeIcon from '../MymeTypeIcon';
import DownloadIcon from 'icons/Download.icon';
import EyeIcon from 'icons/Eye.icon';
import GroupUsersIcon from 'icons/GroupUsers.icon';
import TruckIcon from 'icons/Truck.icon';
import TrashIcon from 'icons/Trash.icon';
import PencilIcon from 'icons/Pencil.icon';
import useSelectAndDeselect from './hooks/useSelectAndDeselect';
import KeyIcon from 'icons/Key.icon';
import ArrowdownIcon from 'icons/Arrowdown.icon';
import ProductsIcon from 'icons/Products.icon';
import useReplaceFile from '../../hooks/useReplaceFile';
import ArrowClockIcon from 'icons/ArrowClock.icon';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import LockopenIcon from 'icons/Lockopen.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import useFindSuggestedDataRoomNodeWithTag from './hooks/useFindSuggestedDataRoomTag';
import {
  getArrayLength,
  getFileIdsForShareableLinks,
  getNodeMimeType,
  isSingleElementArray,
} from 'utils';
import useNodes from 'hooks/useNodes';
import useDisplayedItemsWithPlaceholders from './hooks/useDisplayedItemsWithPlaceholder';
import CreateShareableLinkContext from 'router/subrouters/DataRoom/providers/CreateShareableLink/CreateShareableLink.context';
import LinkIcon from 'icons/Link.icon';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import Counter from 'components/Counter';
import { COUNTER_VARIANTS } from 'components/Counter/Counter.component';
import ResolvePopup from 'components/ResolvePopup';
import useUploadingPlaceholders from './hooks/useUploadingPlaceholders';
import { getMimeTypeByNameExtension } from 'uploadFileUtils';
import StarIcon from 'icons/Star.icon';
import UnstarDocumentsModal from '../UnstarDocumentsModal';
import { FILE_SECURITY } from 'router/subrouters/DataRoom/models/DataRoom';
import {
  isBasicSelected,
  isMaximumSelected,
} from 'router/subrouters/DataRoom/utils';
import DataRoomNodeActionsContext from '../../providers/DataRoomNodeActions/DataRoomNodeActions.context';

import './DataRoomDocList.styles.scss';
import './DataRoomDocList.styles.responsive.scss';

export type DataRoomDocListProps = {
  className?: string;
};

export const SORTING_VALUES = {
  sortName: 'sortName',
  '-sortName': '-sortName',
  modifiedAt: 'modifiedAt',
  '-modifiedAt': '-modifiedAt',
  numberOfDownloads: 'numberOfDownloads',
  '-numberOfDownloads': '-numberOfDownloads',
  numberOfViews: 'numberOfViews',
  '-numberOfViews': '-numberOfViews',
  viewedDate: 'viewedDate',
  '-viewedDate': '-viewedDate',
  numberOfMembersWithAccess: 'numberOfMembersWithAccess',
  '-numberOfMembersWithAccess': '-numberOfMembersWithAccess',
  size: 'size',
  '-size': '-size',
  childrenCount: 'childrenCount',
  '-childrenCount': '-childrenCount',
  isStarred: 'isStarred',
  '-isStarred': '-isStarred',
};

const DataRoomDocList: React.FC<DataRoomDocListProps> = (props) => {
  const { className } = props;

  const {
    isAdmin,
    isBuySide,
    hasIndexGenerated,
    doesntHaveAccess,
    canUserCreateShareableLinks,
    dataRoom: { _id: dataRoomId },
    openBuyQaDrawer,
    setSelectedForQAndADrawerForDrawer,
  } = useContext(DataRoomContext);
  const {
    currentlyDisplayedNodes,
    areDocsLoading,
    documentRoute,
    baseDocsPath,
    areInsightsShown,
    buySidePreviewMember,
    canTagFiles,
    baseLevelNodes,
    setCurrentlySelectedFolder,
    setCurrentlyDisplayedNodes,
  } = useContext(DataRoomDocumentsContext);
  const {
    selected,
    isAllSelected,
    isSingleSelected,
    hasAuthorizedActionsForAllSelected,
    canShareFiles,
    shouldRename,
    shouldAllowBulkActions,
    isSingleSelectedFolder,
    isSingleSelectedVideo,
    canDownloadSelected,
    isSelectedMode,
    onSelect,
    removeAll,
    selectAll,
    updateSelected,
    updateSelectedValue,
  } = useContext(DataRoomSelectedDocumentsContext);
  const { openCreateShareableLink } = useContext(CreateShareableLinkContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { openRename, openMove, openManageAccess, openTag, onTagSet } =
    useContext(DataRoomNodeActionsContext);

  const { documentId } = useParams<{ documentId: string }>();

  const { placeholders, displayedNodeIdsUploading } =
    useUploadingPlaceholders(documentId);

  const displayedItemsWithPlaceholders = useDisplayedItemsWithPlaceholders(
    currentlyDisplayedNodes,
    placeholders,
  );

  const dataRoomTagBanner = useFindSuggestedDataRoomNodeWithTag(
    currentlyDisplayedNodes,
  );

  // Only the hidden input is needed for `onReplaceClick` to open the file
  // dialog, so it is rendered once for the list rather than once per row.
  const { onReplaceClick, getInputProps } = useReplaceFile(
    isSingleSelected ? selected[0] : null,
  );

  const {
    draggableOverlayRef,
    selectionRectRef,
    docListRef,
    isDragging,
    isSelectioningOnDrag,
    isRequestPending,
    setIsDragging,
    setIsRequestPending,
  } = useSelectAndDeselect();

  const { onDocDelete, DeleteDocumentsModal } = useDeleteFile({
    setIsRequestPending,
  });

  const isPhablet = useMatchMedia('isPhablet');

  const { t } = useTranslation();

  const { getCopyByNodeType } = useNodes(selected);

  const onShareableLink = useCallback(() => {
    openCreateShareableLink(getFileIdsForShareableLinks(selected));
  }, [selected, openCreateShareableLink]);

  const { setQueryParam, params } = useQueryParams<Params>();

  const [unstarDocuments, unstarDocumentsRef] = useCallbackRef<ModalRef>(null);

  const classes = classNames('data-room-doc-list', className);

  const isBasicSecurityFirstSelected = isBasicSelected(
    selected?.[0]?.fileSecurity,
  );

  const prevNode = useMemo(
    () =>
      documentRoute.length > 1 ? documentRoute[documentRoute.length - 2] : null,
    [documentRoute],
  );

  const onSelectAll = useCallback(() => {
    if (isAllSelected) {
      removeAll();
    } else {
      selectAll();
    }
  }, [isAllSelected, removeAll, selectAll]);

  // Folders with many nodes render a row per node, so anything done per row has
  // to be O(1) and referentially stable, otherwise every row re-renders on
  // every interaction.
  const selectedIds = useMemo(
    () => new Set(selected.map(({ _id }) => _id)),
    [selected],
  );

  const isFolderEmpty = useMemo(
    () =>
      !getArrayLength(currentlyDisplayedNodes) &&
      !getArrayLength(placeholders.files) &&
      !getArrayLength(placeholders.folders),
    [currentlyDisplayedNodes, placeholders],
  );

  const showLevelUp = useMemo(
    () => !!documentRoute.length,
    [documentRoute.length],
  );

  const onActionMenuDelete = useCallback(
    () => onDocDelete(selected),
    [onDocDelete, selected],
  );

  const onChangeSecureDownload = useCallback(async () => {
    if (!isSingleSelected || isSingleSelectedVideo || isSingleSelectedFolder)
      return;

    const node = {
      ...selected[0],
      fileSecurity: isBasicSecurityFirstSelected
        ? FILE_SECURITY.ADVANCED
        : FILE_SECURITY.BASIC,
    };

    try {
      await dataRoomApi.nodes.changeIsSecuredDownload(
        node._id,
        node.fileSecurity,
      );

      setCurrentlyDisplayedNodes((old) =>
        old.map((el) => (el._id === node._id ? { ...el, ...node } : el)),
      );

      updateSelectedValue(node);

      popSuccess(
        t(
          isBasicSelected(node.fileSecurity)
            ? 'disableRemoteTerminationSucceess'
            : 'enableRemoteTerminationSuccess',
          { fileName: node.name },
        ),
      );
    } catch (e) {
      popServerError(e);
    }
  }, [
    isSingleSelected,
    isSingleSelectedFolder,
    isSingleSelectedVideo,
    selected,
    isBasicSecurityFirstSelected,
    setCurrentlyDisplayedNodes,
    updateSelectedValue,
    t,
    popSuccess,
    popServerError,
  ]);

  const shouldChangeTag = useMemo(
    () => isSingleSelected && canTagFiles && !isSingleSelectedFolder,
    [canTagFiles, isSingleSelected, isSingleSelectedFolder],
  );

  const { download } = useFileDownload(dataRoomId, baseLevelNodes);

  const handleDownload = useCallback(
    (nodes: DataRoomNode[]) => {
      download(nodes);
      if (isSingleElementArray(nodes)) removeAll();
    },
    [download, removeAll],
  );

  const onDownloadNode = useCallback(
    (node: DataRoomNode) => handleDownload([node]),
    [handleDownload],
  );

  const onNodeQandA = useCallback(
    (node: DataRoomNode) => {
      openBuyQaDrawer();
      setSelectedForQAndADrawerForDrawer(node);
    },
    [openBuyQaDrawer, setSelectedForQAndADrawerForDrawer],
  );

  const onNodeDragging = useCallback(
    (value: boolean) => {
      if (hasAuthorizedActionsForAllSelected) setIsDragging(value);
    },
    [hasAuthorizedActionsForAllSelected, setIsDragging],
  );

  // The bulk actions point the list's shared drawers at the current selection.
  const onRenameSelected = useCallback(
    () => openRename(selected),
    [openRename, selected],
  );

  const onMoveSelected = useCallback(
    () => openMove(selected),
    [openMove, selected],
  );

  const onManageAccessSelected = useCallback(
    () => openManageAccess(selected),
    [openManageAccess, selected],
  );

  const onTagSelected = useCallback(
    () => openTag(selected[0]),
    [openTag, selected],
  );

  const shareLabel = getCopyByNodeType(
    'shareItems',
    'shareFolders',
    'shareFiles',
    getArrayLength(selected),
  );

  const dropdownOptionsBulk = useMemo(() => {
    const selectedLength = getArrayLength(selected);

    if (!shouldAllowBulkActions || !selectedLength) return null;

    return [
      ...(canShareFiles
        ? [
            {
              label: shareLabel,
              onClick: onManageAccessSelected,
              icon: <GroupUsersIcon strokeWidth={2} />,
            },
          ]
        : []),
      ...(canShareFiles && canUserCreateShareableLinks
        ? [
            {
              label: t('createSecureLink'),
              onClick: onShareableLink,
              icon: <LinkIcon />,
            },
          ]
        : []),
      ...(hasAuthorizedActionsForAllSelected
        ? [
            {
              label: t('move'),
              onClick: onMoveSelected,
              icon: <TruckIcon />,
            },
          ]
        : []),
      ...(shouldRename
        ? [
            {
              label: t('rename'),
              onClick: onRenameSelected,
              icon: <PencilIcon />,
            },
          ]
        : []),
      ...(canDownloadSelected
        ? [
            {
              label: t('download'),
              onClick: () => handleDownload(selected),
              icon: <DownloadIcon />,
            },
          ]
        : []),
      ...(shouldChangeTag
        ? [
            {
              label: t('tagFile'),
              onClick: onTagSelected,
              icon: <ProductsIcon />,
            },
          ]
        : []),
      ...(isSingleSelected &&
      !isSingleSelectedFolder &&
      !isSingleSelectedVideo &&
      hasAuthorizedActionsForAllSelected
        ? [
            {
              label: t(
                isBasicSecurityFirstSelected
                  ? 'enableRemoteTermination'
                  : 'disableRemoteTermination',
              ),
              onClick: onChangeSecureDownload,
              icon: isBasicSecurityFirstSelected ? (
                <LockclosedIcon />
              ) : (
                <LockopenIcon />
              ),
            },
            {
              label: t('addNewVersion'),
              onClick: onReplaceClick,
              icon: <ArrowClockIcon />,
            },
          ]
        : []),
      ...(hasAuthorizedActionsForAllSelected
        ? [
            {
              label: t('delete'),
              onClick: onActionMenuDelete,
              icon: <TrashIcon />,
              alert: true,
            },
          ]
        : []),
    ];
  }, [
    shouldAllowBulkActions,
    hasAuthorizedActionsForAllSelected,
    shareLabel,
    selected,
    shouldRename,
    isSingleSelected,
    isSingleSelectedFolder,
    isSingleSelectedVideo,
    shouldChangeTag,
    onManageAccessSelected,
    onMoveSelected,
    onRenameSelected,
    onTagSelected,
    canDownloadSelected,
    canUserCreateShareableLinks,
    canShareFiles,
    isBasicSecurityFirstSelected,
    onActionMenuDelete,
    t,
    onReplaceClick,
    onChangeSecureDownload,
    onShareableLink,
    handleDownload,
  ]);

  const updateNode = useCallback(
    (node: DataRoomNode) => {
      setCurrentlyDisplayedNodes((prev) =>
        prev.map((n) => (n._id === node._id ? node : n)),
      );
    },
    [setCurrentlyDisplayedNodes],
  );

  const DraggableOverlayCard = useMemo(() => {
    if (!isDragging || !selected.length || !hasAuthorizedActionsForAllSelected)
      return null;

    const numOfSelected = selected.length;

    const displayNode = selected?.[numOfSelected - 1];

    return (
      <div ref={draggableOverlayRef} className="draggable-overlay">
        <MymeTypeIcon
          mimeType={getNodeMimeType(displayNode)}
          isLocked={isMaximumSelected(displayNode.fileSecurity)}
        />
        <p>{displayNode.name}</p>
        <Counter
          count={numOfSelected}
          isPositionAbsolute
          variant={COUNTER_VARIANTS.PRIMARY}
        />
      </div>
    );
  }, [
    draggableOverlayRef,
    hasAuthorizedActionsForAllSelected,
    isDragging,
    selected,
  ]);

  const generateSortableSpan = useCallback(
    (keyword: keyof typeof SORTING_VALUES, copy: React.ReactNode) => {
      const selected = [
        SORTING_VALUES[keyword],
        SORTING_VALUES[`-${keyword}` as keyof typeof SORTING_VALUES],
      ].includes(params.sort);

      const transcending = SORTING_VALUES[keyword] === params.sort;
      return (
        <span
          className={classNames('keep-on-phone', 'sorting-button', keyword, {
            selected,
            transcending,
          })}
          onClick={() => {
            setQueryParam(
              'sort',
              params.sort === SORTING_VALUES[keyword]
                ? SORTING_VALUES[`-${keyword}` as keyof typeof SORTING_VALUES]
                : SORTING_VALUES[keyword],
            );
          }}
        >
          {copy}
          <div className="sorting-arrows">
            {selected ? (
              <ArrowdownIcon className="arrow" />
            ) : (
              <>
                <SortIcon className="chevron first" />
                <SortIcon className="chevron" />
              </>
            )}
          </div>
        </span>
      );
    },

    [params, setQueryParam],
  );

  const renderNode = useCallback(
    (node: DataRoomNode, isSelected: boolean, suggestedTag) => {
      const isUploading = displayedNodeIdsUploading.some(
        (_id) => _id === node._id,
      );

      return (
        <div
          key={node._id}
          className={classNames('document-wrapper', {
            'is-loading': isUploading,
          })}
          data-node-id={node._id}
        >
          <DataRoomDocument
            node={node}
            isUploading={isUploading}
            updateNode={updateNode}
            suggestedTag={suggestedTag}
            dropdownOptions={dropdownOptionsBulk}
            isRequestPending={isRequestPending}
            onDeleteDocument={setCurrentlyDisplayedNodes}
            onSelect={isPhablet ? updateSelected : onSelect}
            isSelectedMode={isSelectedMode}
            isSelected={isSelected}
            setIsDragging={onNodeDragging}
            onQandA={onNodeQandA}
            areInsightsShown={areInsightsShown}
            onDownload={onDownloadNode}
            onTagSet={onTagSet}
            canEnterEditMode={isSelected && isSingleSelected}
            canTagFiles={canTagFiles}
            onViewFileModalOpen={removeAll}
            onUnstarDocuments={unstarDocuments?.open}
            onDocDelete={onDocDelete}
          />
        </div>
      );
    },
    [
      areInsightsShown,
      canTagFiles,
      displayedNodeIdsUploading,
      dropdownOptionsBulk,
      isPhablet,
      isRequestPending,
      isSelectedMode,
      isSingleSelected,
      onDownloadNode,
      onNodeDragging,
      onNodeQandA,
      onSelect,
      onTagSet,
      removeAll,
      unstarDocuments,
      setCurrentlyDisplayedNodes,
      updateNode,
      updateSelected,
      onDocDelete,
    ],
  );

  const renderPlaceholder = useCallback(
    ({ _id, isPlaceholderFolder, type, name }, index: number) => (
      <div key={_id + index} className="document-wrapper is-loading">
        <div className="data-room-document uploading">
          <div className="data-room-document__content">
            <div className="data-room-document__left">
              <MymeTypeIcon
                mimeType={
                  isPlaceholderFolder
                    ? undefined
                    : type || getMimeTypeByNameExtension(name)
                }
              />
              <div className="data-room-document__info">
                <p className="data-room-document__name">{name}</p>
              </div>
            </div>
            <div className="data-room-document__right">
              <span className="uploading-text">{t('uploading')}...</span>
            </div>
          </div>
        </div>
      </div>
    ),
    [t],
  );

  return (
    <div className={classes} ref={docListRef}>
      <input {...getInputProps()} />

      {!isFolderEmpty && (
        <div className="data-room-doc-list__heading">
          <div>
            {isPhablet && isSelectedMode && (
              <Checkbox
                onChange={onSelectAll}
                checked={!!selected.length}
                className={classNames({ mixed: !isAllSelected })}
              />
            )}

            {hasIndexGenerated && (
              <span className="index">
                <KeyIcon />
              </span>
            )}
            {generateSortableSpan('sortName', t('aToZ'))}
          </div>
          <div>
            {isAdmin &&
              !buySidePreviewMember &&
              generateSortableSpan(
                'isStarred',
                <span className="star-document">
                  <StarIcon />
                </span>,
              )}
            {isAdmin &&
              areInsightsShown &&
              generateSortableSpan(
                'numberOfDownloads',
                <DownloadIcon className="copy-icon" />,
              )}
            {isAdmin &&
              areInsightsShown &&
              generateSortableSpan(
                'numberOfViews',
                <EyeIcon className="copy-icon" />,
              )}

            {isBuySide && generateSortableSpan('viewedDate', t('viewed'))}

            {(isAdmin || isBuySide) &&
              generateSortableSpan('numberOfMembersWithAccess', t('access'))}

            <ResolvePopup
              onHover
              className="direct-children"
              popupContent={t('folderItemCountsFiles')}
            >
              {generateSortableSpan('childrenCount', `${t('files')} (#)`)}
            </ResolvePopup>

            {generateSortableSpan('size', t('size'))}
            {generateSortableSpan('modifiedAt', t('modified'))}
          </div>
        </div>
      )}

      {DraggableOverlayCard}

      {isSelectioningOnDrag && (
        <div
          ref={selectionRectRef}
          className="data-room-doc-list__selection-rect"
        />
      )}

      {areDocsLoading ? (
        <Loader />
      ) : (
        <>
          {displayedItemsWithPlaceholders.folders.map((item: any, index) => {
            if (item.isPlaceholder) {
              return renderPlaceholder(item, index);
            }

            const node = item;
            const isSelected = selectedIds.has(node._id);
            const suggestedTag =
              dataRoomTagBanner?._id === node._id
                ? dataRoomTagBanner?.tag
                : null;

            return renderNode(node, isSelected, suggestedTag);
          })}

          {displayedItemsWithPlaceholders.files.map((item: any, index) => {
            if (item.isPlaceholder) {
              return renderPlaceholder(
                item,
                index + displayedItemsWithPlaceholders.folders.length,
              );
            }

            const node = item as DataRoomNode;
            const isSelected = selectedIds.has(node._id);
            const suggestedTag =
              dataRoomTagBanner?._id === node._id
                ? dataRoomTagBanner?.tag
                : null;

            return renderNode(node, isSelected, suggestedTag);
          })}
        </>
      )}

      {!doesntHaveAccess && isSelectedMode && (
        <DataRoomActionMenu
          onClose={removeAll}
          onDelete={onActionMenuDelete}
          onRename={onRenameSelected}
          onMove={onMoveSelected}
          onManage={onManageAccessSelected}
          onDownload={() => handleDownload(selected)}
          onShareFiles={onShareableLink}
        />
      )}

      <UnstarDocumentsModal ref={unstarDocumentsRef} />

      {isFolderEmpty && !areDocsLoading && <DataRoomDocEmpty />}

      {showLevelUp && !areDocsLoading && (
        <Link
          className="link-back"
          onClick={() => setCurrentlySelectedFolder(prevNode)}
          to={`${baseDocsPath}${prevNode ? prevNode._id : ''}`}
        >
          <ArrowupIcon />
          {t('backUpALevel')}
        </Link>
      )}

      {DeleteDocumentsModal}
    </div>
  );
};

export default DataRoomDocList;
